// 验证者列表 + 统计 + 地图（原 /validators 页内容）
import { useGetValidatorsEvents } from '@mysten/core';
import { useSuiClientQuery } from '@mysten/dapp-kit';
import { Suspense, lazy, useMemo } from 'react';

import { CpcSection, CpcSectionTitle } from '~/components/cpc/CpcPageHero';
import { ErrorBoundary } from '~/components/error-boundary/ErrorBoundary';
import { DelegationAmount } from '~/components/validator/DelegationAmount';
import { useCpcValidatorsApy } from '~/hooks/useCpcValidatorsApy';
import { useT } from '~/i18n/LocaleContext';
import { validatorsTableData } from '~/pages/validators/Validators';
import { Banner } from '~/ui/Banner';
import { Card } from '~/ui/Card';
import { PlaceholderTable } from '~/ui/PlaceholderTable';
import { Stats } from '~/ui/Stats';
import { TableCard } from '~/ui/TableCard';
import { CPC_SYMBOL } from '~/utils/cpcCoin';
import { formatDailyYieldPercent, formatMistAsDgc } from '~/utils/explorerFormat';

const ValidatorMap = lazy(() => import('~/components/validator-map'));

export function CpcValidatorsSection() {
	const t = useT();
	const { data, isPending, isSuccess, isError } = useSuiClientQuery('getLatestSuiSystemState');
	const validators = data?.activeValidators ?? [];
	const numberOfValidators = validators.length;

	const {
		data: validatorEvents,
		isPending: validatorsEventsLoading,
		isError: validatorEventError,
	} = useGetValidatorsEvents({
		limit: numberOfValidators || null,
		order: 'descending',
	});

	const { apyMap, avgApy, isUsingEstimates } = useCpcValidatorsApy(validators);

	const totalStaked = useMemo(
		() => validators.reduce((acc, cur) => acc + BigInt(cur.stakingPoolSuiBalance ?? 0), 0n),
		[validators],
	);

	const lastEpochRewardOnAllValidators = useMemo(() => {
		if (!validatorEvents) return null;
		let totalRewards = 0;
		for (const { parsedJson } of validatorEvents) {
			totalRewards += Number((parsedJson as { pool_staking_reward: string }).pool_staking_reward);
		}
		return totalRewards;
	}, [validatorEvents]);

	const validatorsTable = useMemo(() => {
		if (!data || !validatorEvents) return null;
		return validatorsTableData(
			data.activeValidators,
			data.atRiskValidators,
			validatorEvents,
			apyMap,
		);
	}, [data, validatorEvents, apyMap]);

	if (isError || validatorEventError) {
		return (
			<CpcSection>
				<Banner variant="error" fullWidth>
					{t('stake.validatorsLoadFailed')}
				</Banner>
			</CpcSection>
		);
	}

	return (
		<div id="validators" className="space-y-4">
			<div className="grid gap-4 lg:grid-cols-2">
				<CpcSection>
					<CpcSectionTitle>{t('stake.validatorsNetwork')}</CpcSectionTitle>
					<div className="grid grid-cols-2 gap-4">
						<Stats
							label={t('stake.networkStake').replace('{symbol}', CPC_SYMBOL)}
							tooltip={t('stake.networkStakeTip')}
							color="dark"
							unavailable={totalStaked <= 0n}
						>
							<span className="font-semibold tabular-nums text-[#38BDF8]">
								{formatMistAsDgc(totalStaked, { compact: true })} {CPC_SYMBOL}
							</span>
						</Stats>
						<Stats
							label={t('stake.avgDailyYield')}
							tooltip={t('stake.avgDailyYieldTip')}
							color="dark"
							unavailable={avgApy == null}
						>
							<span className="font-semibold tabular-nums text-[#38BDF8]">
								{formatDailyYieldPercent(avgApy, { estimated: isUsingEstimates })}
							</span>
						</Stats>
						<Stats
							label={t('stake.lastEpochReward')}
							tooltip={t('stake.lastEpochRewardTip')}
							color="dark"
							unavailable={lastEpochRewardOnAllValidators === null}
						>
							<DelegationAmount
								amount={
									typeof lastEpochRewardOnAllValidators === 'number'
										? lastEpochRewardOnAllValidators
										: 0n
								}
								isStats
							/>
						</Stats>
						<Stats
							label={t('stake.activeValidators')}
							tooltip={t('stake.activeValidatorsTip')}
							color="dark"
						>
							<span className="font-semibold tabular-nums text-white">{numberOfValidators}</span>
						</Stats>
					</div>
				</CpcSection>

				<CpcSection className="min-h-[240px]">
					<CpcSectionTitle>{t('stake.validatorMap')}</CpcSectionTitle>
					<ErrorBoundary>
						<Suspense fallback={<p className="text-sm text-[#94a3b8]">{t('stake.mapLoading')}</p>}>
							<ValidatorMap minHeight={200} />
						</Suspense>
					</ErrorBoundary>
				</CpcSection>
			</div>

			<Card bg="dark" spacing="lg">
				<CpcSectionTitle>{t('stake.allValidators')}</CpcSectionTitle>
				<ErrorBoundary>
					{(isPending || validatorsEventsLoading) && (
						<PlaceholderTable
							rowCount={12}
							rowHeight="13px"
							colHeadings={[t('stake.colName'), t('stake.colStake'), t('stake.colDaily')]}
							colWidths={['220px', '160px', '100px']}
						/>
					)}
					{isSuccess && validatorsTable?.data && (
						<TableCard data={validatorsTable.data} columns={validatorsTable.columns} sortTable />
					)}
				</ErrorBoundary>
			</Card>
		</div>
	);
}
